import '@Pages/BrandEquity/BrandEquity.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AreaChartDataRow,
  BrandImpactData,
  BusinessImpactData,
  GroupByOptionsBrandEquityMetric,
  ImplicationData,
  IncrementalRoasTakeaways,
  NextBestDollar,
  PaidMediaDataRow,
  RevenueImpactTakeaways,
  SaturationDataUnformatted,
  TotalRevenueDecomposition,
  WeeklyIncrementalRoas,
  WeeklyRevenueDecomposition
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { capitalizeWords, MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import {
  BrandEquityJobRun,
  BrandEquityScenarioPlanningModel
} from '@blisspointmedia/bpm-types/dist/ScenarioPlanning';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner';
import { ScenarioPlanning } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanning';
import { useFeatureMapping } from '@Pages/MMM/hooks/useFeatureMapping';
import { MmmRunSelector } from '@Pages/MMM/MmmRunSelector/MmmRunSelector';
import {
  BRAND_EQUITY_RUN_KPI,
  brandEquityToModelRuns,
  markRunsWithoutScenarioModel
} from '@Pages/MMM/MmmRunSelector/mmm-run-selector-adapters';
import { useMmmPage } from '@Pages/MMM/use-mmm-page';
import { datadogRum } from '@datadog/browser-rum';
import { useSetError } from '@src/redux/modals';
import { reportError } from '@src/utils/errorReporting';
import { awaitJSON, MetricsLambdaFetch } from '@src/utils/fetch-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  getMostRecentDateAndKPI,
  hasClientFacingItem,
  hasClientFacingOptions,
  isBrandImpactDataComplete,
  isBusinessImpactDataComplete,
  removeDashPFromEndOfMetric,
  transformTakeawaysImpactRevenue
} from './BrandEquityUtils';
import { BrandImpact } from './BrandImpact/BrandImpact';
import { getCentralMeasureType } from './BrandImpact/BrandImpactUtils';
import { BusinessImpact } from './BusinessImpact/BusinessImpact';
import { isQueryVolumeMetric } from './metricNormalization';

const TabKey = {
  BRAND_IMPACT: 'brand-impact',
  BUSINESS_IMPACT: 'business-impact',
  SCENARIO_PLANNING: 'scenario-planning'
} as const;
type TabKey = (typeof TabKey)[keyof typeof TabKey];

const isTabKey = (value: string): value is TabKey =>
  (Object.values(TabKey) as string[]).includes(value);

export const GROUP_BY_OPTIONS = [
  { label: 'Channel', value: 'channel' },
  { label: 'Channel, Platform', value: 'channel, platform' },
  {
    label: 'Channel, Platform, Tactic, Brand/Nonbrand',
    value: 'channel, platform, tactic, brand/nonbrand'
  }
];

interface GroupByOptions {
  label: string;
  value: string;
  s3Path: string;
  branch: string;
  output_file_version?: number;
}

// Raw row shape returned by the BE response-curves lambda, before it is reshaped into
// `SaturationDataUnformatted`. The endpoint returns either a flat array or a
// `{ paid, organic }` split depending on the metric.
interface SaturationResponseRow {
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
  name: string;
  effective_adstock_spend: number;
  effective_response: number;
  median: number;
  mean: number;
  previous_qtr_average_value: number;
}

type SaturationResponse =
  SaturationResponseRow[] | { paid?: SaturationResponseRow[]; organic?: SaturationResponseRow[] };

export const BrandEquity = () => {
  const { tab, goToTab, navs, isInternal } = useMmmPage<TabKey>({
    baseURL: 'brand-equity',
    navs: [
      { key: TabKey.BRAND_IMPACT, label: 'Brand Impact' },
      { key: TabKey.BUSINESS_IMPACT, label: 'Business Impact' },
      { key: TabKey.SCENARIO_PLANNING, label: 'Scenario Planning', requiresInternal: true }
    ]
  });
  const { cid: company } = useCompanyInfo();
  const { data: featureMapping } = useFeatureMapping(company ?? '', 'BrandEquity');
  const { pathname } = useLocation();
  const [groupByLevel, setGroupByLevel] = useState('channel');
  const [groupByMetric, setGroupByMetric] = useState('');
  const isAmazonQueryVolume = pathname.includes('amazon');

  const [modelRunDate, setModelRunDate] = useState<string>('');
  const [groupByOptionsBrandImpact, setGroupByOptionsBrandImpact] = useState<
    GroupByOptionsBrandEquityMetric[]
  >([]);
  // Each run's scenario model, carried on the run rows (stage-two rows) for the Scenario Planning tab.
  const [runModels, setRunModels] = useState<BrandEquityScenarioPlanningModel[]>([]);
  const [groupByOptionsBrandImpactAll, setGroupByOptionsBrandImpactAll] = useState<
    Record<string, GroupByOptionsBrandEquityMetric[]>
  >({});
  const [groupByOptionsBusinessImpactAll, setGroupByOptionsBusinessImpactAll] = useState<
    Record<string, GroupByOptionsBrandEquityMetric[]>
  >({});

  const [centralMeasure, setCentralMeasure] = useState<MedianOrMean>(MedianOrMean.MEDIAN);
  const [isFetchingData, setIsFetchingData] = useState(false);

  const setError = useSetError();

  const [brandImpactData, setBrandImpactData] = useState<BrandImpactData>({
    weeklyBrandHealthTimeSeries: [],
    modelOverview: {},
    weeklyBrandHealthAreaChartSpend: {},
    implicationsData: {},
    paidMediaAreaChart: {},
    paidMediaBarChart: {},
    spendAndEffectBarChart: {},
    nextBestDollarBarChart: {},
    saturationData: {},
    decayData: {}
  });

  const [businessImpactData, setBusinessImpactData] = useState<BusinessImpactData>({
    weeklyBrandHealthTimeSeries: [],
    modelOverview: {
      client: '',
      kpi: '',
      kpi_type: '',
      date_granularity: '',
      refresh_date: '',
      refresh_timestamp: '',
      train_start_date: '',
      train_end_date: '',
      test_start_date: '',
      test_end_date: '',
      train_size: 0,
      test_size: 0,
      train_rsquared: 0,
      test_rsquared: 0,
      test_mape: 0,
      test_mae: 0,
      n_observations: 0,
      mean_spend: 0,
      median_spend: 0
    },
    weeklyRevenueTimeSeries: [],
    totalRevenueDecomposition: [],
    weeklyRevenueDecomposition: [],
    revenueImpactTakeaways: {
      name: '',
      MediaDrivenBrandEquity: 0,
      BrandEquityRevenue: 0,
      Total: 0,
      BrandEquityRevenuePercent: 0
    },
    weeklyIncrementalRoas: [],
    incrementalRoasTakeaways: { paidMediaSpend: 0, spendInducedBrandRevenue: 0, iRoasTtm: 0 },
    saturationData: {},
    decayData: {}
  });

  const toggleBrandEquityRun = useCallback(
    async (modelDate: string, checked: boolean, kpi?: string) => {
      try {
        await MetricsLambdaFetch('/toggleBrandEquityRun', {
          method: 'POST',
          body: {
            company,
            date: modelDate,
            client_facing: checked,
            kpi: kpi || ''
          }
        });
        setGroupByOptionsBrandImpact(prevOptions =>
          prevOptions.map(item => ({
            ...item,
            client_facing: !checked
          }))
        );
        setGroupByOptionsBrandImpactAll(prevOptions => {
          const updatedOptions = { ...prevOptions };

          if (updatedOptions[modelDate]) {
            updatedOptions[modelDate] = updatedOptions[modelDate].map(item => ({
              ...item,
              client_facing: !checked
            }));
          }
          return updatedOptions;
        });
        setGroupByOptionsBusinessImpactAll(prevOptions => {
          const updatedOptions = { ...prevOptions };

          if (updatedOptions[modelDate]) {
            updatedOptions[modelDate] = updatedOptions[modelDate].map(item => ({
              ...item,
              client_facing: !checked
            }));
          }
          return updatedOptions;
        });
      } catch (e) {
        setError({
          message: e.message,
          reportError: e
        });
      }
    },
    [company, setError]
  );

  const fetchBrandImpactData = useCallback(
    async (companyId: string, brandImpactOptions: GroupByOptions[]) => {
      try {
        // Fetch all 6 endpoints for every metric in parallel, then aggregate.
        const allMetricData = await Promise.all(
          brandImpactOptions.map(async currentMetric => {
            const params = {
              company: companyId,
              branch: currentMetric.branch,
              kpi: currentMetric.s3Path,
              date: modelRunDate,
              outputFileVersion: currentMetric.output_file_version
            };

            const [overviewRes, kpiRes, spendEffectRes, implicationsRes, responseRes, decayRes] =
              await Promise.all([
                MetricsLambdaFetch('/getBEModelOverviewData', { params }),
                MetricsLambdaFetch('/getBEKpiData', { params }),
                MetricsLambdaFetch('/getBESpendEffectOverTimeData', { params }),
                MetricsLambdaFetch('/getBEImplicationsData', { params }),
                MetricsLambdaFetch('/getBEResponseCurvesData', { params }),
                MetricsLambdaFetch('/getBEDecayCurvesData', { params })
              ]);

            const [overview, kpiData, spendEffect, implications, response, decay] =
              await Promise.all([
                awaitJSON(overviewRes),
                awaitJSON(kpiRes),
                awaitJSON(spendEffectRes),
                awaitJSON(implicationsRes),
                awaitJSON<SaturationResponse>(responseRes),
                awaitJSON(decayRes)
              ]);

            return { currentMetric, overview, kpiData, spendEffect, implications, response, decay };
          })
        );

        // weeklyBrandHealthTimeSeries — merge all metrics by date
        const weeklyBrandHealthTimeSeries = allMetricData.reduce(
          (total, { currentMetric, kpiData }) => {
            kpiData.forEach(row => {
              const existingRow = total.find(r => r.date === row.date);
              if (existingRow) {
                Object.assign(existingRow, { [currentMetric.label]: row.actual_kpi });
              } else {
                total.push({ date: row.date, [currentMetric.label]: row.actual_kpi });
              }
            });
            return total;
          },
          [] as any[]
        );

        // modelOverview — keyed by metric label
        const modelOverview = allMetricData.reduce((acc, { currentMetric, overview }) => {
          const modelOverviewDestructured = Object.assign(
            {},
            ...overview.map(row => ({ [row.parameter]: row.value }))
          );
          return { ...acc, [currentMetric.label]: modelOverviewDestructured };
        }, {});

        // weeklyBrandHealthAreaChartSpend — from cptbDirect (g_cptb equivalent)
        const weeklyBrandHealthAreaChartData = allMetricData.reduce(
          (acc, { currentMetric, spendEffect }) => ({
            ...acc,
            [currentMetric.label]: spendEffect.cptbDirect.map(row => ({
              date: row.date,
              channel: row.channel,
              platform: row.platform,
              'brand/nonbrand': row.brand_nonbrand,
              tactic: row.tactic,
              spend: row.raw_spend
            }))
          }),
          {}
        );

        // paidMediaAreaChart / paidMediaBarChart / spendAndEffectBarChart — from cptb (c_cptb equivalent)
        const finalAreaChartData = {};
        const finalBarChartData = {};
        const finalSpendAndEffect = {};

        allMetricData.forEach(({ currentMetric, spendEffect }) => {
          const series = spendEffect.cptb.map(row => ({
            date: row.date,
            totalPositiveResponse: row.effective_predicted_response_normalized_to_observed,
            effectivePredictedResponse: row.effective_predicted_response,
            spendShare: row.effective_spend,
            channel: row.channel,
            tactic: row.tactic,
            platform: row.platform,
            'brand/nonbrand': row.brand_nonbrand,
            name: capitalizeWords(row.name.split(', ').join(' - '))
          }));

          const result = (series as PaidMediaDataRow[]).reduce(
            (acc, curr) => {
              const { date, totalPositiveResponse, name } = curr;
              if (!acc[date]) {
                acc[date] = { date, PaidMedia: 0, OtherFactors: 0, Total: 0 };
              }
              if (name === 'Covariates') {
                acc[date].OtherFactors += totalPositiveResponse || 0;
              } else {
                acc[date].PaidMedia += totalPositiveResponse || 0;
              }
              acc[date].Total += totalPositiveResponse || 0;
              return acc;
            },
            {} as Record<string, AreaChartDataRow>
          );

          const areaChartData = Object.values(result);
          const barChartUnformatted = areaChartData.reduce(
            (acc, row) => {
              acc.PaidMediaSum += row.PaidMedia;
              acc.OtherFactorsSum += row.OtherFactors;
              acc.TotalSum += row.Total;
              return acc;
            },
            { PaidMediaSum: 0, OtherFactorsSum: 0, TotalSum: 0 }
          );

          finalAreaChartData[currentMetric.label] = areaChartData;
          finalBarChartData[currentMetric.label] = {
            PaidMedia: barChartUnformatted.PaidMediaSum / barChartUnformatted.TotalSum,
            OtherFactors: barChartUnformatted.OtherFactorsSum / barChartUnformatted.TotalSum,
            key: currentMetric.label
          };
          finalSpendAndEffect[currentMetric.label] = series;
        });

        // implicationsData + nextBestDollarBarChart — both from the same implications fetch
        const implicationsData: Record<string, ImplicationData> = {};
        const finalNextBestDollar: Record<string, NextBestDollar[]> = {};

        allMetricData.forEach(({ currentMetric, implications }) => {
          const [firstImplication] = implications
            .filter(row => !row.dark_last_quarter)
            .map(row => ({
              spendToIncreaseOnePercent:
                row.best_incremental_portfolio_spend_total_for_additional_unit
            }));
          implicationsData[currentMetric.label] = firstImplication;

          finalNextBestDollar[currentMetric.label] = implications.map(row => ({
            label: 'Spend',
            channel: row.channel,
            tactic: row.tactic,
            'brand/nonbrand': row.brand_nonbrand,
            name: capitalizeWords(row.name.split(', ').join(' - ')),
            platform: row.platform,
            value: row.best_incremental_portfolio_allocation_for_additional_unit
          }));
        });

        // saturationData
        const saturationData = allMetricData.reduce((acc, { currentMetric, response }) => {
          const responseRows = Array.isArray(response) ? response : (response.paid ?? []);
          return {
            ...acc,
            [currentMetric.label]: responseRows.map((row): SaturationDataUnformatted => ({
              channel: row.channel,
              platform: row.platform,
              tactic: row.tactic,
              'brand/nonbrand': row.brand_nonbrand,
              spend: row.effective_adstock_spend,
              response: row.effective_response,
              name: row.name,
              median: row.median,
              mean: row.mean || row.previous_qtr_average_value
            }))
          };
        }, {});

        const decayData = allMetricData.reduce(
          (acc, { currentMetric, decay }) => ({ ...acc, [currentMetric.label]: decay }),
          {}
        );

        setBrandImpactData({
          weeklyBrandHealthTimeSeries,
          modelOverview,
          weeklyBrandHealthAreaChartSpend: weeklyBrandHealthAreaChartData,
          implicationsData,
          paidMediaAreaChart: finalAreaChartData,
          paidMediaBarChart: finalBarChartData,
          spendAndEffectBarChart: finalSpendAndEffect,
          nextBestDollarBarChart: finalNextBestDollar,
          saturationData,
          decayData
        });

        const satDataKeys = Object.keys(saturationData);
        if (satDataKeys.length) {
          setCentralMeasure(getCentralMeasureType(saturationData[satDataKeys[0]][0]));
        }
      } catch (e) {
        setError({ message: "Couldn't load brand equity data", reportError: e });
      }
    },
    [modelRunDate, setError]
  );

  const fetchBusinessImpactData = useCallback(
    async (
      companyId: string,
      businessImpactOptions: GroupByOptions[],
      brandImpactOptions: GroupByOptions[]
    ) => {
      try {
        const biMetric = businessImpactOptions[0];
        const biParams = {
          company: companyId,
          branch: biMetric.branch,
          kpi: biMetric.s3Path,
          date: modelRunDate,
          outputFileVersion: biMetric.output_file_version
        };

        // Weekly brand health time series comes from stage-one (brand impact) metrics.
        const brandHealthKpiResponses = await Promise.all(
          brandImpactOptions.map(m =>
            MetricsLambdaFetch('/getBEKpiData', {
              params: {
                company: companyId,
                branch: m.branch,
                kpi: m.s3Path,
                date: modelRunDate,
                outputFileVersion: m.output_file_version
              }
            }).then(awaitJSON)
          )
        );

        const weeklyBrandHealthTimeSeries = brandHealthKpiResponses.reduce(
          (total, kpiData, idx) => {
            const currentMetric = brandImpactOptions[idx];
            kpiData.forEach(row => {
              const existingRow = total.find(r => r.date === row.date);
              if (existingRow) {
                Object.assign(existingRow, { [currentMetric.label]: row.actual_kpi });
              } else {
                total.push({ date: row.date, [currentMetric.label]: row.actual_kpi });
              }
            });
            return total;
          },
          [] as any[]
        );

        const [
          overviewRes,
          kpiRes,
          brandTimeSeriesRes,
          trailingEstRes,
          incrementalRoasRes,
          decayRes,
          responseRes
        ] = await Promise.all([
          MetricsLambdaFetch('/getBEModelOverviewData', { params: biParams }),
          MetricsLambdaFetch('/getBEKpiData', { params: biParams }),
          MetricsLambdaFetch('/getBEBrandTimeSeriesDecomp', { params: biParams }),
          MetricsLambdaFetch('/getBETrailingEst', { params: biParams }),
          MetricsLambdaFetch('/getBEIncrementalRoas', { params: biParams }),
          MetricsLambdaFetch('/getBEDecayCurvesData', { params: biParams }),
          MetricsLambdaFetch('/getBEResponseCurvesData', { params: biParams }).catch(e => {
            reportError(e, { operation: 'brandEquity.responseCurves' });
            return null;
          })
        ]);

        const [overview, kpiData, brandTimeSeries, trailingEst, incrementalRoas, decay, response] =
          await Promise.all([
            awaitJSON(overviewRes),
            awaitJSON(kpiRes),
            awaitJSON(brandTimeSeriesRes),
            awaitJSON(trailingEstRes),
            awaitJSON(incrementalRoasRes),
            awaitJSON(decayRes),
            responseRes ? awaitJSON<SaturationResponse>(responseRes) : Promise.resolve(null)
          ]);

        const modelOverview = Object.assign(
          {},
          ...overview.map(row => ({ [row.parameter]: row.value }))
        );

        const weeklyRevenueTimeSeries = kpiData.map(row => ({
          date: row.date,
          revenue: row.actual_kpi
        }));

        const totalRevenueDecomposition: TotalRevenueDecomposition[] = trailingEst.map(row => {
          const brandAndSpend = row.brand_rev + row.spend_induced_brand_rev;
          const otherFactors = row.response - brandAndSpend;
          const total = brandAndSpend + otherFactors;
          return {
            name: 'revenue',
            MediaDrivenBrandEquity: row.spend_induced_brand_rev,
            NonMediaDrivenBrandEquity: row.brand_rev,
            OtherFactors: otherFactors,
            MediaDrivenBrandEquityPercent: row.spend_induced_brand_rev / total,
            NonMediaDrivenBrandEquityPercent: row.brand_rev / total,
            OtherFactorsPercent: otherFactors / total
          };
        });

        const weeklyRevenueDecomposition: WeeklyRevenueDecomposition[] = brandTimeSeries.reduce(
          (acc: any[], row: any) => {
            const { response: responseVal, channel_group, value } = row;
            const unformattedDate = row.Date || row.date;
            let existingRow = acc.find(item => item.date === unformattedDate);

            if (!existingRow) {
              existingRow = {
                date: unformattedDate,
                OtherFactors: responseVal,
                total: responseVal,
                MediaDrivenBrandEquity: 0,
                NonMediaDrivenBrandEquity: 0
              };
              acc.push(existingRow);
            }

            if (channel_group === 'Spend Induced Brand Effect') {
              existingRow.OtherFactors -= value;
              existingRow.MediaDrivenBrandEquity = value;
            } else if (channel_group === 'Brand Effect') {
              existingRow.OtherFactors -= value;
              existingRow.NonMediaDrivenBrandEquity = value;
            }

            return acc;
          },
          []
        );

        const revenueImpactTakeaways: RevenueImpactTakeaways = transformTakeawaysImpactRevenue(
          brandTimeSeries.map(row => ({
            date: row.Date || row.date,
            channelGroup: row.channel_group,
            response: row.response,
            value: row.value
          }))
        );

        const weeklyIncrementalRoas: WeeklyIncrementalRoas[] = incrementalRoas.map(row => ({
          date: row.date,
          RevenueFromMediaDrivenBrandEquity: row.media_driven_brand_effect,
          Spend: row.total_spend,
          IncrementalRoas: row.iroas
        }));

        const incrementalRoasTakeaways: IncrementalRoasTakeaways = {
          paidMediaSpend: trailingEst[0]?.paid_media_spend,
          spendInducedBrandRevenue: trailingEst[0]?.spend_induced_brand_rev,
          iRoasTtm: trailingEst[0]?.iroas_ttm
        };

        const channelMapping = {
          awareness_p: 'Awareness',
          buzz_p: 'Buzz',
          consideration_p: 'Consideration',
          intent_p: 'Intent',
          gqv: isAmazonQueryVolume ? 'AQV' : 'GQV',
          TotalIndexedQueryVolume: 'TotalIndexedQueryVolume',
          yougov__awareness: 'Awareness',
          yougov__buzz: 'Buzz',
          yougov__consideration: 'Consideration',
          yougov__purchase_intent: 'Purchase Intent',
          yougov__ad_awareness: 'Ad Awareness'
        };

        const brandEquityChannels = [
          'awareness',
          'buzz',
          'consideration',
          'intent',
          'awareness_p',
          'buzz_p',
          'consideration_p',
          'intent_p',
          'awareness_14day',
          'buzz_14day',
          'consideration_14day',
          'intent_14day',
          'awareness_ne_14day',
          'buzz_ne_14day',
          'consideration_ne_14day',
          'intent_ne_14day',
          'percent_positive_ad_awareness',
          'percent_positive_awareness',
          'percent_positive_buzz',
          'percent_positive_consideration',
          'percent_positive_purchase_intent',
          'gqv',
          'gqv_indexed',
          'gqv indexed',
          'yougov__awareness',
          'yougov__buzz',
          'yougov__consideration',
          'yougov__purchase_intent',
          'yougov__ad_awareness'
        ];

        const finalDecayData = decay.filter(
          row =>
            brandEquityChannels.includes(row.name?.toLowerCase()) ||
            brandEquityChannels.includes(row.channel?.toLowerCase()) ||
            row.name?.toLowerCase() === 'totalindexedqueryvolume'
        );

        let responseRows: SaturationResponseRow[];
        if (!response) {
          responseRows = [];
        } else if (Array.isArray(response)) {
          responseRows = response;
        } else {
          responseRows = response.organic ?? [];
        }

        const finalSaturationData = responseRows
          .filter(
            row =>
              brandEquityChannels.includes(row.channel?.toLowerCase()) ||
              row.name?.toLowerCase() === 'totalindexedqueryvolume'
          )
          .map(row => ({
            name:
              channelMapping[row.channel?.toLowerCase()]?.toLowerCase() ?? row.name.toLowerCase(),
            channel: channelMapping[row.channel?.toLowerCase()],
            tactic: row.tactic,
            'brand/nonbrand': row.brand_nonbrand,
            spend: row.effective_adstock_spend,
            response: row.effective_response,
            median: row.median,
            mean: row.mean || row.previous_qtr_average_value
          }));

        setBusinessImpactData({
          weeklyBrandHealthTimeSeries,
          modelOverview,
          weeklyRevenueTimeSeries,
          totalRevenueDecomposition,
          weeklyRevenueDecomposition,
          revenueImpactTakeaways,
          weeklyIncrementalRoas,
          incrementalRoasTakeaways,
          saturationData: finalSaturationData,
          decayData: finalDecayData
        });
      } catch (e) {
        setError({ message: "Couldn't load brand equity data", reportError: e });
      }
    },
    [modelRunDate, isAmazonQueryVolume, setError]
  );

  useEffect(() => {
    (async () => {
      try {
        const companyToFetch = company;
        const allRuns = await MetricsLambdaFetch('/getBrandEquityJobRuns', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          params: {
            company: companyToFetch
          }
        });
        const parsedAllRuns = await awaitJSON<BrandEquityJobRun[]>(allRuns);
        setRunModels(
          parsedAllRuns.flatMap(row =>
            !row.stage_one && row.scenarioPlanning ? [row.scenarioPlanning] : []
          )
        );

        const brandImpactOptions: Record<string, GroupByOptionsBrandEquityMetric[]> = {};
        const businessImpactOptions: Record<string, GroupByOptionsBrandEquityMetric[]> = {};
        parsedAllRuns.forEach(row => {
          const {
            date: currentDate,
            kpi,
            client_facing,
            branch,
            stage_one,
            output_file_version
          } = row;

          if (!brandImpactOptions[currentDate]) {
            brandImpactOptions[currentDate] = [];
          }
          if (!businessImpactOptions[currentDate]) {
            businessImpactOptions[currentDate] = [];
          }

          const option = {
            label: removeDashPFromEndOfMetric(kpi),
            value: removeDashPFromEndOfMetric(kpi),
            s3Path: kpi,
            kpi,
            client_facing,
            branch,
            stage_one,
            output_file_version
          };
          if (stage_one) {
            brandImpactOptions[currentDate].push(option);
          } else {
            businessImpactOptions[currentDate].push(option);
          }
        });

        if (
          Object.keys(brandImpactOptions).length === 0 &&
          Object.keys(businessImpactOptions).length === 0
        ) {
          setError({
            message: 'There are no Brand Equity runs for this client'
          });
          return;
        }
        if (
          !hasClientFacingOptions(brandImpactOptions) &&
          !hasClientFacingOptions(businessImpactOptions) &&
          !isInternal
        ) {
          setError({
            message: 'There are no released runs for this client'
          });
          return;
        }

        setGroupByOptionsBrandImpactAll(brandImpactOptions);
        setGroupByOptionsBusinessImpactAll(businessImpactOptions);

        const { date: mostRecentDate, kpi } = getMostRecentDateAndKPI(brandImpactOptions);
        setModelRunDate(mostRecentDate);
        setGroupByMetric(kpi);
      } catch (e) {
        setError({ message: 'Failed to fetch incrementality job statuses', reportError: e });
      }
    })();
  }, [company, isInternal, setError]);

  useEffect(() => {
    if (!modelRunDate) {
      return;
    }
    const brandImpact = groupByOptionsBrandImpactAll[modelRunDate] || [];
    const businessImpact = groupByOptionsBusinessImpactAll[modelRunDate] || [];
    const updatedBrandImpact = brandImpact.map(item =>
      item.value === 'Awareness' ? { ...item, label: 'Aided Awareness' } : item
    );
    setGroupByOptionsBrandImpact(updatedBrandImpact);

    if (brandImpact.length > 0 && businessImpact.length > 0) {
      setIsFetchingData(true);
      Promise.all([
        fetchBrandImpactData(company, brandImpact),
        fetchBusinessImpactData(company, businessImpact, brandImpact)
      ]).finally(() => setIsFetchingData(false));
    }
  }, [
    modelRunDate,
    company,
    groupByOptionsBrandImpactAll,
    groupByOptionsBusinessImpactAll,
    fetchBrandImpactData,
    fetchBusinessImpactData
  ]);

  // The per-metric data maps (saturationData, paidMediaAreaChart, etc.) are keyed
  // by metric, so every child looks up its slice with map[groupByMetric]. If the
  // selected metric isn't one of the current run's metrics — e.g. after switching
  // to a run with a different metric set — those lookups resolve to undefined and
  // the child charts crash. Reset the selection to a metric that exists in the run.
  useEffect(() => {
    if (!modelRunDate) {
      return;
    }
    const brandImpact = groupByOptionsBrandImpactAll[modelRunDate] || [];
    if (brandImpact.length === 0) {
      return;
    }
    const availableMetrics = brandImpact.map(item => item.value);
    if (!availableMetrics.includes(groupByMetric)) {
      setGroupByMetric(brandImpact[0].value);
    }
  }, [modelRunDate, groupByOptionsBrandImpactAll, groupByMetric]);

  const resolvedCompany = company === 'tinuititest' ? 'rover' : company;

  const brandEquityKpiRuns = useMemo(
    () => brandEquityToModelRuns(groupByOptionsBrandImpactAll, { isInternal }),
    [groupByOptionsBrandImpactAll, isInternal]
  );
  const canToggleRuns = isInternal && !['tinuititest', 'tinuitisearchtest'].includes(company);
  // The page's selected run and its scenario model (carried on the run rows): one selector drives every
  // tab. A run with no model (an admin-branch run, `latest`) shows as unavailable on Scenario Planning.
  const sharedRun = useMemo(
    () =>
      modelRunDate ? { model: runModels.find(model => model.date === modelRunDate) } : undefined,
    [modelRunDate, runModels]
  );
  const selectorKpiRuns = useMemo(
    () =>
      tab === TabKey.SCENARIO_PLANNING
        ? markRunsWithoutScenarioModel(brandEquityKpiRuns, run =>
            runModels.some(model => model.date === run.refreshDate)
          )
        : brandEquityKpiRuns,
    [tab, brandEquityKpiRuns, runModels]
  );

  return (
    <Page
      title='Brand Equity'
      pageType='Brand Equity'
      navs={navs}
      selectedNav={tab}
      onNav={nav => {
        if (isTabKey(nav)) {
          goToTab(nav);
        } else {
          // Unreachable by construction — `navs` and the header derive from the same `TabKey`
          // set. Logged (not silently swallowed) so a future nav-wiring regression surfaces.
          datadogRum.addError(new Error(`BrandEquity: ignored nav to unknown tab "${nav}"`));
        }
      }}
      actions={
        <div className='brand-equity-right-actions'>
          {modelRunDate && (
            <div className='brand-equity-archive-actions'>
              <MmmRunSelector
                kpiRuns={selectorKpiRuns}
                kpi={BRAND_EQUITY_RUN_KPI}
                selectedDate={modelRunDate}
                isInternal={isInternal}
                hideKpi
                menuAlign='end'
                onChange={({ run }) => setModelRunDate(run.refreshDate)}
                onToggleClientFacing={
                  canToggleRuns
                    ? run =>
                        toggleBrandEquityRun(
                          run.refreshDate,
                          hasClientFacingItem(groupByOptionsBrandImpactAll[run.refreshDate])
                        )
                    : undefined
                }
              />
            </div>
          )}
        </div>
      }
      subHeader={
        tab === TabKey.BRAND_IMPACT &&
        groupByOptionsBrandImpact.length > 0 && (
          <div className='sub-header-item-at-end'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              className={
                isInternal ? 'brand-impact-level-dropdown internal' : 'brand-impact-level-dropdown'
              }
              title='Brand Health Metrics'
              onChange={setGroupByMetric}
              options={groupByOptionsBrandImpact.map(kpi => ({
                ...kpi,
                label: isQueryVolumeMetric(kpi.value) ? 'GQV' : kpi.label,
                section: kpi.client_facing ? 'Client-Facing' : 'Admin-Only'
              }))}
              value={groupByMetric}
            />
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              className='brand-equity-level-dropdown'
              onChange={setGroupByLevel}
              options={GROUP_BY_OPTIONS}
              value={groupByLevel}
            />
          </div>
        )
      }
    >
      {isFetchingData ? (
        <FullPageSpinner />
      ) : (
        <div className='full-page-router brand-equity-page'>
          {tab === TabKey.BRAND_IMPACT &&
            isBrandImpactDataComplete(brandImpactData) &&
            groupByMetric !== undefined &&
            businessImpactData.incrementalRoasTakeaways !== undefined &&
            businessImpactData.incrementalRoasTakeaways !== null && (
              <BrandImpact
                company={resolvedCompany}
                groupByMetric={groupByMetric}
                groupByMetricOptions={groupByOptionsBrandImpact}
                groupByLevel={groupByLevel}
                brandImpactData={brandImpactData}
                incrementalRoasTakeAways={businessImpactData.incrementalRoasTakeaways}
                queryVolumeLongLabel={
                  isAmazonQueryVolume ? 'Amazon query volume' : 'Google query volume'
                }
                queryVolumeShortLabel={isAmazonQueryVolume ? 'AQV' : 'GQV'}
                centralMeasure={centralMeasure}
              />
            )}
          {tab === TabKey.BUSINESS_IMPACT &&
            isBusinessImpactDataComplete(businessImpactData) &&
            groupByMetric !== undefined && (
              <BusinessImpact
                company={resolvedCompany}
                businessImpactData={businessImpactData}
                groupByLevel={groupByLevel}
                queryVolumeLongLabel={
                  isAmazonQueryVolume ? 'Amazon query volume' : 'Google query volume'
                }
                queryVolumeShortLabel={isAmazonQueryVolume ? 'AQV' : 'GQV'}
                centralMeasure={centralMeasure}
              />
            )}
          {tab === TabKey.SCENARIO_PLANNING && sharedRun && (
            <ScenarioPlanning
              modelType='BrandEquity'
              featureMapping={featureMapping}
              sharedRun={sharedRun}
              runModels={runModels}
            />
          )}
        </div>
      )}
    </Page>
  );
};
